<template>
  <div>

    <FormLabel
      v-if="label"
      :label="label"
      :label-hint="labelHint"/>

    <div
      v-if="showReUpload || !path"
      class="flex cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border border-slate-200 bg-opacity-10 px-4 py-6 transition-all hover:bg-slate-50"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <VideoCameraIcon
        v-if="btnBackground && btnForeground"
        class="h-8 w-8" :style="`color:${darkestHex(btnBackground, btnForeground)};`"/>
      <img v-else src="https://resources.businessdraft.com/bd-camera.svg" class="h-8 w-8" alt="video icon"/>
      <div class="flex items-center gap-2 text-sm">
        <FormButton size="sm" :color="btnBackground && btnForeground ? 'hex' : 'gradient'"
                    :hex-background="btnBackground"
                    :hex-foreground="btnForeground"
                    @click="showRecord = true" bold>
          Record Video
        </FormButton>
        <span class="text-slate-300">or</span>
        <label :for="id">
          <FormButton
            medium
            size="sm"
            color="secondary"
            @click="$refs.fileInput.click()"
          >Upload File
          </FormButton
          >
          <input
            ref="fileInput"
            type="file"
            accept="video/mp4,video/x-m4v,video/*"
            @change="onChange"
            :id="id"
            class="absolute h-px w-px opacity-0"
          />
        </label>
      </div>
    </div>
    <div
      v-else
      class="flex items-center gap-4 rounded-lg border border-slate-100 bg-slate-50 bg-opacity-50 px-4 py-3 text-sm"
    >
      <video
        v-if="path"
        :src="path"
        class="w-24 rounded-lg max-h-[6rem]"
        autoplay
        muted
        loop
        @click="showPathFullscreen = true"
      />
      <div class="cursor-pointer">
        <button type="button" @click="showPathFullscreen = true" class="block font-bold text-slate-900">
          Video Uploaded
        </button>
        <button type="button" @click="showPathFullscreen = true" class="block text-slate-400 hover:underline">
          Watch Video
        </button>
      </div>
      <HoverTooltip class="ml-auto" tip="Record new video" placement="top">
        <button type="button" @click="showReUpload = true"
                class="flex cursor-pointer items-center gap-2 p-1 text-slate-500 hover:text-slate-900 hover:underline">
          <ArrowPathIcon class="h-4 w-4"/>
        </button>
      </HoverTooltip>
      <UtilityDeleteIcon @delete="deleteVideo" hide-bg class="self-center py-1"/>
    </div>
    <UtilityVideoRecorder
      v-if="showRecord"
      @update:file="$emit('update:file', $event)"
      :max-seconds="maxSeconds"
      @update:path="$emit('update:path', $event)"
      @close="closeVideoRecorder"
    />
    <UtilityFullscreenVideoPlayer
      v-if="showPathFullscreen"
      :src="path"
      @update:src="showPathFullscreen = false"
    />
  </div>
</template>

<script setup>
import FormLabel from "./FormLabel.vue";
import FormButton from "./FormButton.vue";
import HoverTooltip from "./HoverTooltip.vue";
import UtilityVideoRecorder from "./UtilityVideoRecorder.vue";
import UtilityFullscreenVideoPlayer from "./UtilityFullscreenVideoPlayer.vue";
import UtilityDeleteIcon from "./UtilityDeleteIcon.vue";
import {ArrowPathIcon, VideoCameraIcon} from "@heroicons/vue/20/solid/index.js";
import {ref} from "vue";
import {darkestHex} from "../helpers/Color";

const emits = defineEmits(["update:file", "update:path"]);
const props = defineProps({
  label: String,
  labelHint: String,
  path: String,
  file: Object,
  id: String,
  maxSeconds: Number,
  btnBackground: String,
  btnForeground: String,
});

const showRecord = ref(false);
const showReUpload = ref(false);
const fileInput = ref(null);
const showPathFullscreen = ref(false);

const dragover = (e) => {
  e.preventDefault();
  e.stopPropagation();

  e.dataTransfer.dropEffect = "copy";
};

const dragleave = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const drop = (e) => {
  e.preventDefault();
  e.stopPropagation();

  const files = e.dataTransfer.files;
  handleFiles(files);
};

const onChange = (e) => {
  const files = e.target.files;
  handleFiles(files);
};

const handleFiles = (files) => {
  if (files.length > 0) {
    const file = files[0];
    validateVideoDuration(file, async () => {
      emits("update:path", URL.createObjectURL(file));
      emits("update:file", file);

      showReUpload.value = false;
    });
  }
};

function validateVideoDuration(file, successCallback) {
  let video = document.createElement("video");
  video.preload = "metadata";
  video.onloadedmetadata = function () {
    window.URL.revokeObjectURL(video.src);

    if (video.duration > props.maxSeconds + 1) {
      toast.error("Video must be less than " + props.maxSeconds + " seconds long");
      return;
    }

    successCallback();
  };

  video.src = URL.createObjectURL(file);
}

const closeVideoRecorder = () => {
  showRecord.value = false;
  showReUpload.value = !props.path;
};

const deleteVideo = () => {
  emits("update:file", null);
  emits("update:path", null);
  showReUpload.value = true;
};
</script>
